import * as L from 'leaflet';
import { Swiper } from 'swiper';

class ArchiveAdresse {
    private section: HTMLElement | null;

    // Toggle
    private toggleButtons: NodeListOf<HTMLButtonElement>;
    private viewContainers: NodeListOf<HTMLElement>;
    private activeView: string = 'list';

    // Map
    private mapElement: HTMLElement | null;
    private locations?: any[];
    private mapInstance: L.Map | null = null;
    private geolocateBtn: HTMLButtonElement | null;
    private notificationElement: HTMLElement | null;
    private notificationTimer: number | null = null;

    // Popin
    private triggerButtons: NodeListOf<HTMLButtonElement>;
    private popins: NodeListOf<HTMLElement>;
    private overlay: HTMLElement | null;
    private closeButtons: NodeListOf<HTMLButtonElement>;

    private debouncedSort: () => void;

    constructor() {
        this.section = document.querySelector('[data-section]');

        this.toggleButtons = document.querySelectorAll('[data-view-toggle-button]');
        this.viewContainers = document.querySelectorAll('[data-view-container]');

        this.mapElement = document.querySelector('[data-map-archive-adresse]') as HTMLElement | null;
        this.geolocateBtn = document.querySelector('[data-geolocate]') as HTMLButtonElement | null;
        this.notificationElement = document.querySelector('[data-map-notification]') as HTMLElement | null;


        this.triggerButtons = document.querySelectorAll('[data-categorie]');
        this.popins = document.querySelectorAll('[data-popin-adresse]');
        this.overlay = document.querySelector('[data-popin-adresse-overlay]');
        this.closeButtons = document.querySelectorAll('[data-popin-adresse-close]');

        this.debouncedSort = this.debounce(this.sortActiveListByDistance, 300);

        if (!this.section || this.toggleButtons.length === 0 || this.viewContainers.length === 0
            || !this.mapElement || !this.geolocateBtn
            || this.triggerButtons.length === 0 || this.popins.length === 0 || !this.overlay) {
            return;
        }
        this._initToggle();

        this.locations = JSON.parse(this.mapElement.dataset.locations || '[]');
        this._initMap();
        this._initSwiper();
        this._initPopin();
    }

    // Toggle
    private _initToggle(): void {
        this.toggleButtons.forEach(button => {
            button.addEventListener('click', () => {
                const viewName = button.dataset.view;
                if (viewName) {
                    this.switchToView(viewName);
                    if (this.section)
                        this.section.dataset.section = viewName
                }
            });
        });
    }

    private switchToView(viewName: string): void {
        if (viewName === this.activeView) {
            return;
        }

        this.activeView = viewName;

        this.toggleButtons.forEach(button => {
            if (button.dataset.view === viewName) {
                button.classList.add('active');
                button.classList.remove('inactive');
            } else {
                button.classList.remove('active');
                button.classList.add('inactive');
            }
        });

        // Met à jour les conteneurs de vue
        this.viewContainers.forEach(container => {
            if (container.id === `view-${viewName}`) {
                container.classList.remove('hidden');
            } else {
                container.classList.add('hidden');
            }
        });

        if (viewName === 'map') {
            if (this.mapInstance) {
                this.mapInstance.invalidateSize();
            }
        }
    }

    // -----------------------------------------
    // Map
    private async _initMap(): Promise<void> {
        if (this.locations?.length === 0) {
            console.warn("Aucune localisation à afficher sur la carte.");
            return;
        }
        try {
            const L = await import('leaflet');
            if (this.mapInstance) {
                this.mapInstance.remove();
            }

            // 1. On initialise la carte SANS setView. Le zoom sera automatique.
            this.mapInstance = L.map(this.mapElement);

            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            }).addTo(this.mapInstance);

            const markerBounds: L.LatLngExpression[] = [];

            // 2. On boucle sur chaque lieu pour créer et placer un marqueur
            this.locations!.forEach(loc => {
                const customIcon = L.icon({
                    iconUrl: loc.icon_url,
                    iconSize: [32, 32],
                    iconAnchor: [16, 32],
                    popupAnchor: [0, -35]
                });

                const marker = L.marker([loc.lat, loc.lng], { icon: customIcon })
                    .addTo(this.mapInstance!)
                    .bindPopup(loc.popup);

                markerBounds.push([loc.lat, loc.lng]);
            });

            // 3. On ajuste le zoom et le centrage pour afficher tous les marqueurs
            if (markerBounds.length > 0) {
                const latSum = this.locations!.reduce((acc, loc) => acc + loc.lat, 0);
                const lngSum = this.locations!.reduce((acc, loc) => acc + loc.lng, 0);
                const center: [number, number] = [latSum / this.locations!.length, lngSum / this.locations!.length];

                this.mapInstance.setView(center, 14);
            }

            this.setupGeolocation();

            this.mapInstance.on('moveend', this.debouncedSort);
        } catch (error) {
            console.error("Erreur lors de l'initialisation de la carte globale :", error);
        }
    }

    private setupGeolocation(): void {
        if (!this.geolocateBtn || !this.mapInstance) {
            return;
        }
        const geolocateIconUrl = this.geolocateBtn.dataset.geolocateIcon;


        this.geolocateBtn.addEventListener('click', () => {
            this.mapInstance?.locate({ setView: true, maxZoom: 16 });
        });

        // Cet événement se déclenche si la géolocalisation réussit
        this.mapInstance.on('locationfound', (e: L.LocationEvent) => {
            const markerOptions: L.MarkerOptions = {};

            if (geolocateIconUrl) {
                const customIcon = L.icon({
                    iconUrl: geolocateIconUrl,
                    iconSize: [32, 32],
                    iconAnchor: [16, 32],
                });
                markerOptions.icon = customIcon;
            }
            L.marker(e.latlng, markerOptions).addTo(this.mapInstance!);
            this.showNotification('success');
        });

        // Cet événement se déclenche si la géolocalisation échoue
        this.mapInstance.on('locationerror', (e: L.ErrorEvent) => {
            this.showNotification('error');
        });
    }

    private showNotification(type: 'success' | 'error'): void {
        if (!this.notificationElement) return;

        const message = this.notificationElement.dataset[type === 'success' ? 'messageSuccess' : 'messageError'];
        const iconUrl = this.notificationElement.dataset[type === 'success' ? 'iconSuccess' : 'iconError'];
        if (this.notificationTimer) {
            clearTimeout(this.notificationTimer);
        }
        this.notificationElement.innerHTML = `
        <div class="btn btn--${type}">
        <img src="${iconUrl}" alt="" width="24" height="24">
            ${message}
        </div>
        `;

        this.notificationElement.classList.add('visible');

        this.notificationTimer = window.setTimeout(() => {
            this.notificationElement?.classList.remove('visible');
        }, 1000);
    }

    private debounce<F extends (...args: any[]) => any>(func: F, delay: number): (...args: Parameters<F>) => void {
        let timeoutId: number | null = null;
        return (...args: Parameters<F>) => {
            const context = this;

            if (timeoutId) {
                clearTimeout(timeoutId);
            }

            timeoutId = window.setTimeout(() => {
                func.apply(context, args);
            }, delay);
        };
    }


    private sortActiveListByDistance(): void {
        if (!this.mapInstance) return;

        // 1. On trouve la pop-in actuellement visible
        const activePopin = document.querySelector('[data-popin-adresse].active') as HTMLElement | null;
        if (!activePopin) {
            return;
        }

        // 2. On trouve la liste et les items UNIQUEMENT dans cette pop-in
        const listContainer = activePopin.querySelector('[data-list-adresses]');
        const listItems = Array.from(activePopin.querySelectorAll('[data-adresse]'));

        if (!listContainer || listItems.length === 0) return;

        const mapCenter = this.mapInstance.getCenter();

        // 3. On trie le tableau des éléments de la pop-in active
        listItems.sort((a, b) => {
            const latA = parseFloat((a as HTMLElement).dataset.lat || '0');
            const lngA = parseFloat((a as HTMLElement).dataset.lng || '0');
            const latB = parseFloat((b as HTMLElement).dataset.lat || '0');
            const lngB = parseFloat((b as HTMLElement).dataset.lng || '0');

            const distanceA = mapCenter.distanceTo(L.latLng(latA, lngA));
            const distanceB = mapCenter.distanceTo(L.latLng(latB, lngB));

            return distanceA - distanceB;
        });

        // 4. On ré-insère les éléments triés dans LEUR PROPRE conteneur
        // (On ne vide pas pour éviter un "flash", appendChild déplace les éléments existants)
        listItems.forEach(item => {
            listContainer.appendChild(item);
        });
    }

    // Swiper
    private _initSwiper(): void {
        const swiperEl = document.querySelector('.archive-adresse__swiper') as HTMLElement;
        if (swiperEl) {
            new Swiper(swiperEl, {
                direction: 'horizontal',
                slidesPerView: 'auto',
                spaceBetween: 8,
                grabCursor: true
            });
        }
    }

    // Popin
    private _initPopin(): void {
        this.triggerButtons.forEach(button => {
            button.addEventListener('click', () => {
                const targetSlug = button.dataset.categorie;
                if (targetSlug) {
                    this.activatePopin(targetSlug);
                }
            });
        });
        this.closeButtons.forEach(button => {
            button.addEventListener('click', () => {
                const popinToClose = button.closest('[data-popin-adresse]') as HTMLElement | null;
                if (popinToClose) {
                    this.closePopin(popinToClose);
                }
            });
        });

        this.overlay?.addEventListener('click', () => this.closeAllPopins());
    }

    private activatePopin(targetSlug: string): void {
        const targetPopin = document.getElementById(targetSlug);
        this.closeAllPopins();

        if (targetPopin) {
            targetPopin.classList.add('active');
            this.overlay?.classList.add('active');
            this.sortActiveListByDistance();
        }
    }

    private closePopin(popin: HTMLElement): void {
        popin.classList.remove('active');
        this.overlay?.classList.remove('active');
    }

    private closeAllPopins(): void {
        this.popins.forEach(p => p.classList.remove('active'));
        this.overlay?.classList.remove('active');
    }

}
export const archiveAdresse = new ArchiveAdresse();
